import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useShoppingList } from '../context/ShoppingListContext';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import api from '../services/api';
import { RECIPE_DETAIL as RD } from '../constants/strings';

export default function useRecipeDetail() {
  const { id, slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [servings, setServings] = useState(4);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareConfirmed, setShareConfirmed] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareError, setShareError] = useState('');
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeAllergen, setActiveAllergen] = useState(null);
  const [cookedLoading, setCookedLoading] = useState(false);
  const [cookedToast, setCookedToast] = useState(null);

  const { addItems, removeItem, items: shoppingItems } = useShoppingList();
  const { setPageTitle } = useBreadcrumb();
  const shareModalCloseRef = useRef(null);

  useEffect(() => {
    if (!activeAllergen) return;
    function handleOutsideClick() { setActiveAllergen(null); }
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [activeAllergen]);

  useEffect(() => {
    if (!showShareModal) return;
    function handleEscape(e) {
      if (e.key === 'Escape') setShowShareModal(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showShareModal]);

  useEffect(() => {
    if (showShareModal && shareModalCloseRef.current) {
      shareModalCloseRef.current.focus();
    }
  }, [showShareModal]);

  useEffect(() => {
    loadRecipe();
  }, [id, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!recipe) return;
    document.title = `${recipe.title} – Strixly Rezept`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', recipe.description || `${recipe.title} – Rezept auf Strixly`);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const canonicalUrl = recipe.slug
      ? `https://strixly.at/rezept/${recipe.slug}`
      : `https://strixly.at/recipe/${recipe.id}`;
    canonical.setAttribute('href', canonicalUrl);
    return () => {
      document.title = 'Strixly – KI-Küchen-Assistent | Kühlschrank scannen & Rezepte finden';
      if (metaDesc) metaDesc.setAttribute('content', 'Strixly ist dein KI-Küchen-Assistent. Kühlschrank scannen, Rezepte finden und Lebensmittelverschwendung reduzieren.');
      canonical?.remove();
    };
  }, [recipe]);

  async function loadRecipe() {
    try {
      let data;
      if (slug) {
        data = await api.getRecipeBySlug(slug);
      } else if (user) {
        data = await api.getRecipe(id);
      } else {
        data = await api.getPublicRecipe(id);
      }
      setRecipe(data.recipe);
      setServings(data.recipe.servings || 4);
      setPageTitle(data.recipe.title);
    } catch (error) {
      console.error('Failed to load recipe:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateShareLink() {
    setShareError('');
    try {
      const data = await api.createShareLink(recipe.id);
      const url = `${window.location.origin}/s/${data.share_token}`;
      setShareLink(url);
      setRecipe(r => ({ ...r, share_token: data.share_token }));
    } catch {
      setShareError(RD.errCreateLink);
    }
  }

  async function handleRevokeShareLink() {
    setShareError('');
    try {
      await api.revokeShareLink(recipe.id);
      setShareLink(null);
      setShareConfirmed(false);
      setShowRevokeConfirm(false);
      setShowShareModal(false);
      setRecipe(r => ({ ...r, share_token: null }));
    } catch {
      setShareError(RD.errRevokeLink);
      setShowRevokeConfirm(false);
    }
  }

  function getShareUrl() {
    return shareLink || `${window.location.origin}/s/${recipe.share_token}`;
  }

  function handleCopyLink() {
    const url = getShareUrl();
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      }).catch(() => fallbackCopy(url));
    } else {
      fallbackCopy(url);
    }
  }

  function fallbackCopy(text) {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    try {
      document.execCommand('copy');
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Clipboard nicht verfügbar — Link ist im Modal sichtbar
    }
    document.body.removeChild(el);
  }

  async function handleNativeShare() {
    try {
      await navigator.share({
        title: recipe.title,
        text: `Schau dir dieses Rezept an: ${recipe.title}`,
        url: getShareUrl(),
      });
    } catch {
      // User abgebrochen oder API nicht verfügbar
    }
  }

  function handlePlatformShare(platform) {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(`Schau dir dieses Rezept an: ${recipe.title}`);
    const links = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      facebook:  `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter:   `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      telegram:  `https://t.me/share/url?url=${url}&text=${text}`,
      linkedin:  `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email:     `mailto:?subject=${encodeURIComponent(recipe.title)}&body=${text}%20${url}`,
    };
    window.open(links[platform], '_blank');
  }

  async function handleCooked() {
    if (cookedLoading) return;
    setCookedLoading(true);
    try {
      const data = await api.markRecipeCooked(recipe.id);
      setCookedToast({ reduced: data.reduced || [] });
      setTimeout(() => setCookedToast(null), 4000);
    } catch {
      // fail-silent
    } finally {
      setCookedLoading(false);
    }
  }

  function handleOpenShareModal() {
    setShareLink(recipe.share_token ? `${window.location.origin}/s/${recipe.share_token}` : null);
    setShareError('');
    setShowRevokeConfirm(false);
    setShowShareModal(true);
  }

  function handleStartDelete() {
    setDeleteError('');
    setShowDeleteConfirm(true);
  }

  async function handleDelete() {
    setDeleteError('');
    try {
      await api.deleteRecipe(recipe.id);
      navigate('/');
    } catch (error) {
      setDeleteError(RD.errDeletePrefix + error.message);
      setShowDeleteConfirm(false);
    }
  }

  function scaledAmount(ingredient) {
    if (!ingredient.amount) return ingredient.amount;
    const base = recipe.servings > 0 ? recipe.servings : servings;
    const factor = servings / base;
    const result = ingredient.amount * factor;
    if (!isFinite(result)) return ingredient.amount;
    return result.toFixed(1).replace('.0', '');
  }

  const isGuest = !user;
  const canEdit = !isGuest && (user?.is_admin || recipe?.user_id === user?.id);
  const tags = Array.isArray(recipe?.tags) ? recipe.tags : [];
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : [];
  const allergens = Array.isArray(recipe?.allergens) ? recipe.allergens : [];

  const backLink = isGuest
    ? { to: '/rezepte', label: '← Alle Rezepte' }
    : location.state?.from === 'editor'
      ? { to: '/my-recipes', label: '← Meine Rezepte' }
      : sessionStorage.getItem('upload_results')
        ? { to: '/upload', label: '← Scan-Ergebnisse' }
        : { to: '/rezepte', label: '← Zurück' };

  return {
    recipe, loading,
    servings, setServings,
    showShareModal, setShowShareModal,
    shareConfirmed, setShareConfirmed,
    shareLink, shareCopied, shareError,
    showRevokeConfirm, setShowRevokeConfirm,
    deleteError,
    showDeleteConfirm, setShowDeleteConfirm,
    activeAllergen, setActiveAllergen,
    cookedLoading, cookedToast,
    shareModalCloseRef,
    handleOpenShareModal, handleStartDelete,
    handleCreateShareLink, handleRevokeShareLink,
    getShareUrl, handleCopyLink, handleNativeShare, handlePlatformShare,
    handleCooked, handleDelete, scaledAmount,
    user, id,
    isGuest, canEdit, tags, steps, allergens, backLink,
    addItems, removeItem, shoppingItems,
  };
}
